<template>
    <CompassModal
        :visible.sync="showModal"
        size="small"
        :title="translations.title"
        class="automation-modal-update-version"
        @closed="close"
    >
        <template #default>
            <template v-if="!loading">
                <p
                    class="automation-modal-update-version__default typographies-paragraph-medium-regular"
                >
                    {{ translations.description }}
                </p>
                <div class="automation-modal-update-version__footer">
                    <CompassButton
                        :label="translations.cancelButton"
                        type="secondary"
                        @click="close"
                    />
                    <CompassButton
                        :label="translations.applyButton"
                        class="automation-modal-update-version__footer__apply-button"
                        @click="updateVersionOfAutomations"
                    />
                </div>
            </template>

            <template v-else>
                <div class="automation-modal-update-version__default__loading">
                    <CompassLoader />
                    <p class="typographies-paragraph-medium-regular">
                        {{ translations.loadingLabel }}
                    </p>
                </div>
                <div class="automation-modal-update-version__footer">
                    <CompassAlert
                        type="neutral"
                        :description="translations.alertDescription"
                        :showClose="false"
                    />
                </div>
            </template>
        </template>
    </CompassModal>
</template>

<script>
import { updateAutomation } from 'api/automation/automationService';
import AutomationMixin from 'mixins/automation/AutomationMixin.js';

export default {
    mixins: [AutomationMixin],
    props: {
        automations: {
            type: Array,
            required: true,
        },
        templates: {
            type: Array,
            required: true,
        },
    },
    emits: ['close'],
    data() {
        return {
            showModal: true,
            loading: false,
            translations: {
                title: this.$t.__('automation.AutomationModalUpdateVersion.title'),
                description: this.$t.__('automation.AutomationModalUpdateVersion.description'),
                cancelButton: this.$t.__('common.actions.cancel'),
                applyButton: this.$t.__('automation.AutomationModalUpdateVersion.apply'),
                loadingLabel: this.$t.__('automation.AutomationModalUpdateVersion.loading'),
                alertDescription: this.$t.__('automation.AutomationModalUpdateVersion.alert.description')
            },
        };
    },
    methods: {
        close() {
            this.$emit('close');
        },
        async updateVersionOfAutomations() {
            this.loading = true;

            try {
                const updatePromises = this.automations.map((automation) => {
                    const metadata = this.prepareData(automation.metadata);
                    const data = {
                        metadata: metadata,
                        applyTemplateUpdate: true
                    };

                    return updateAutomation(automation.id, data)
                        .then(() => ({ success: true, automationId: automation.id }))
                        .catch((error) => {
                            if (this.errorsHandler) {
                                this.errorsHandler.logException(error);
                            }
                            return { success: false, error: true, automationId: automation.id };
                        });
                });

                const results = await Promise.all(updatePromises);
                
                const successCount = results.filter(r => r.success).length;
                const failureCount = results.filter(r => r.error).length;
                const updateSucceeded = failureCount === 0;

                this.showModal = false;
                this.close();
                
                EventBus.emit('automation-updated-version', {
                    success: updateSucceeded,
                    successCount,
                    failureCount,
                    total: this.automations.length
                });
            } catch (error) {
                if (this.errorsHandler) {
                    this.errorsHandler.logException(error);
                }
                this.showModal = false;
                this.close();
                EventBus.emit('automation-updated-version', {
                    success: false,
                    successCount: 0,
                    failureCount: this.automations.length,
                    total: this.automations.length
                });
            } finally {
                this.loading = false;
            }
        },
        prepareData(metadata) {
            const templates = this.templates.filter((template) => template.id === metadata.template.id);
            if (templates.length === 0) {
                return metadata;
            }

            const template = templates[0];
            const metaTemplate = metadata.template;
            metaTemplate.version = template.version;

            const data = {
                template: metaTemplate,
                wizard: metadata.wizard,
                wizard_bindings: template.wizard_bindings,
            }

            const schemaValues = {};

            // Now we use wizard_schema instead of wizard_steps
            if (!template.wizard_schema) {
                return metadata;
            }

            for (const [indexSchema, step] of Object.entries(template.wizard_schema)) {
                if (!step.fields) {
                    continue;
                }

                // For each field, we check value in new structure (wizard_schema) and old (wizard_steps) for retrocompat
                for (let [indexField, field] of Object.entries(step.fields)) {
                    if (metadata.wizard_schema && field.id === metadata.wizard_schema[indexSchema]?.fields[indexField]?.id) {
                        schemaValues[field.id] = this.getFieldProps(metadata.wizard_schema[indexSchema]?.fields[indexField])
                    }
                }
            }

            this.prepareFormDataForSubmission({ metadata: metadata }, template, schemaValues);
            data.wizard_schema = metadata.wizard_schema

            return data;
        },
        getFieldProps(currentField) {
            switch (currentField['type']) {
                case 'select':
                case 'pipeline_step_opp':
                    return currentField.props.select.value || null;
                case 'date_time':
                case 'hidden':
                    return currentField.value || null;
                case 'date':
                case 'time':
                case 'text':
                case 'textarea':
                case 'amount':
                case 'percent':
                    return currentField.props.input.value || null;
                case 'priority_task':
                    return currentField.props.iconButton.value || null;
                default:
                    break;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-modal-update-version {
    &__default {
        display: flex;
        margin: 0 0 var(--spacing-4);

        &__loading {
            display: flex;
            align-items: center;
            margin: 0 0 var(--spacing-4) 0;
            gap: var(--spacing-0);
        }
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        padding: 0 0 var(--spacing-4) 0;

        &__apply-button {
            margin-left: var(--spacing-3);
        }
    }
}
</style>
